import type { Fraction } from '@mimir/common/util/fractionType';
import { approximateFractionValue, divideFractionBy } from '@mimir/common/util/integerMath';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import mapValues from 'lodash/mapValues';
import type { BufferCoordinator } from './sourceBufferCoordinator';

/**
 * Iterates over buffered time ranges to find the next media segment that has
 * not yet been buffered.
 */
class UnbufferedSegmentIterator {
  rangeIndex = 0;
  last = 0;
  constructor(readonly buffered: TimeRanges, private segmentDuration: Fraction) {
  }

  toString() {
    const parts: string[] = [];
    for (let i = 0; i < this.buffered.length; ++i) {
      parts.push(this.buffered.start(i) + '-' + this.buffered.end(i));
    }
    return parts.join(',');
  }

  /**
   * Return the next segment greater or equal to the given start stegment which
   * has not yet been fully buffered.
   */
  nextUnbufferedSegment(start: number) {
    if (start < this.last) {
      throw Error('Out of order segment requests');
    }
    this.last = start;
    const fuzz = approximateFractionValue(divideFractionBy(this.segmentDuration, 4));

    while (true) {
      if (this.rangeIndex >= this.buffered.length) {
        return start;
      }

      const bufferedSegmentStart = Math.ceil(
        ((this.buffered.start(this.rangeIndex) - fuzz) *
          this.segmentDuration.denominator) /
          this.segmentDuration.numerator
      );
      const bufferedSegmentEnd = Math.floor(
        ((this.buffered.end(this.rangeIndex) + fuzz) *
          this.segmentDuration.denominator) /
          this.segmentDuration.numerator
      );

      if (bufferedSegmentEnd <= start) {
        /*
          This buffered range pertains to segments preceding the start segment
          we want to test. Advance to next buffered range and retest.
        */
        ++this.rangeIndex;
        continue;
      }

      if (bufferedSegmentStart <= start) {
        /*
          The start segment is fully contained in the buffered range, and thus
          does not need to be fetched. Return the segment immediately following
          the current buffered range as a candidate for buffering.
        */

        return bufferedSegmentEnd;
      }

      /*
        The start segment is not in any of the buffered ranges. Return it as a
        candidate for buffering.
      */
      return start;
    }
  }
}

/**
 * A media artifact segment stream that is a candidate for having the next media
 * segment to fetch and buffer.
 */
export interface StreamCandidate {
  streamInfo: {
    streamId: StreamRoleId;
    coordinator: BufferCoordinator;
  };
  unbufferedSegmentIterator: UnbufferedSegmentIterator;
}

export function makeStreamCandidates(coordinatorsByStreamId: Partial<Record<StreamRoleId, BufferCoordinator>>, segmentDuration: Fraction): StreamCandidate[] {
  const iterators = mapValues(coordinatorsByStreamId, coordinator => new UnbufferedSegmentIterator(coordinator!.buffer.buffered, segmentDuration));
  return Object.entries(iterators).map(entry => ({ streamInfo: { streamId: entry[0] as StreamRoleId, coordinator: coordinatorsByStreamId[entry[0] as StreamRoleId]! }, unbufferedSegmentIterator: entry[1]! }));
}
